import { ApiClientError, createApiClient, readAuthErrorCode } from '@liquid-spirit/api/client';
import { resolveStoredAuthToken } from '../utils/storage';
import maintainerMetrics from './maintainerMetrics';
import { beginMutation, endMutation } from './mutationActivity';
import { refreshSessionViaBridge, expireSessionViaBridge } from './authBridge';
import { API_BASE_URL } from '../config/apiBase';
import { credentialScope as credentialScopeFor } from '../utils/credentialScope';
import { getActiveQueryOwner, isActiveQueryCredential, ownsQueryScope, isAccessDenied, invalidatePrivateServiceCaches } from '../queries/queryOwnership';

// A 401 that means "attach a (fresh) token and try again" — covers expired
// access tokens AND the brief post-login window where a request can race ahead
// of the token being persisted. Treating these as refreshable lets the single
// refresh-and-retry path recover silently instead of tearing down the session.
export const isRefreshableAuthResponse = (response, payload) => {
  if (response?.status !== 401) return false;
  const code = readAuthErrorCode(response, payload);
  if (code) return code === 'AUTH_TOKEN_EXPIRED' || code === 'AUTH_TOKEN_MISSING';
  const message = String(payload?.message || payload?.error || payload || '').toLowerCase();
  return (
    message.includes('token has expired')
    || message.includes('jwt expired')
    || message.includes('session expired')
    || message.includes('invalid or expired token')
    || message.includes('no token')
    || message.includes('not authenticated')
    || message.includes('unauthorized')
  );
};

/**
 * @typedef {'payload' | 'data' | 'raw'} ApiContract
 */

/**
 * @typedef {Object} ApiResult
 * @property {number} status
 * @property {any} payload
 * @property {Response} response
 */

export const resolveAuthToken = (explicitToken) => {
  return resolveStoredAuthToken(explicitToken);
};

/**
 * @typedef {Object} ApiRequestOptions
 * @property {string} [method]
 * @property {Object} [params]
 * @property {any} [body]
 * @property {Object} [headers]
 * @property {string|Object|null} [token]
 * @property {boolean} [auth]
 * @property {boolean} [jsonContentType]
 * @property {AbortSignal} [signal]
 * @property {ApiContract} [contract]
 * @property {boolean} [throwOnError]
 * @property {(ctx: Object) => Promise<any>} [cacheAdapter]
 * @property {boolean} [skipMetrics]
 * @property {boolean} [skipMutationActivity]
 */

/**
 * Shared request primitive for service modules.
 * Supports an optional cache adapter for GET calls.
 */
const clientOptions = {
  baseUrl: API_BASE_URL,
  jsonContentTypeRequiresBody: false,
  onMetricStart: ({ path, method }) => maintainerMetrics.recordApiRequestMetricStart({
    path, method, source: 'apiClient',
  }),
  onMetricEnd: (metric, details) => maintainerMetrics.recordApiRequestMetricEnd(metric, details),
};

// Bind recovery to the credential actually sent, rather than whichever account
// is active when the response eventually arrives. Keep shared transport intact.
const requestForOwner = async (path, options) => {
  const headers = options.headers instanceof Headers
    ? Object.fromEntries(options.headers.entries()) : { ...options.headers };
  const authorizationKey = Object.keys(headers).find(key => key.toLowerCase() === 'authorization');
  const explicitAuthorization = authorizationKey ? String(headers[authorizationKey]) : null;
  let requestToken = explicitAuthorization !== null
    ? (/^Bearer\s+(.+)$/i.exec(explicitAuthorization)?.[1] || null)
    : resolveAuthToken(options.token);
  let requestOwner = getActiveQueryOwner();
  if (!isActiveQueryCredential(requestToken)) requestOwner = null;
  let credentialScope = credentialScopeFor(explicitAuthorization && !requestToken
    ? explicitAuthorization : requestToken);
  let hasRefreshed = false;
  const isCurrent = () => Boolean(requestOwner && ownsQueryScope(requestOwner)
    && isActiveQueryCredential(requestToken));
  const client = createApiClient({
    ...clientOptions,
    resolveAuthToken: () => {
      if (hasRefreshed && !isCurrent()) throw new Error('Request session is no longer active');
      return requestToken;
    },
    isExpiredAuthResponse: (response, payload) => isCurrent() && isRefreshableAuthResponse(response, payload),
    refreshSession: async () => {
      if (!isCurrent()) throw new Error('Request session is no longer active');
      const refreshed = await refreshSessionViaBridge();
      // The refresh operation itself is generation guarded by AuthProvider.
      // Only adopt the exact credential it returned, never another login.
      const nextOwner = getActiveQueryOwner();
      if (!nextOwner || nextOwner.actorId !== requestOwner.actorId
        || nextOwner.communityId !== requestOwner.communityId
        || !isActiveQueryCredential(refreshed.token)) throw new Error('Request session is no longer active');
      requestOwner = nextOwner;
      requestToken = refreshed.token;
      credentialScope = credentialScopeFor(requestToken);
      hasRefreshed = true;
      if (authorizationKey) headers[authorizationKey] = `Bearer ${requestToken}`;
      return refreshed;
    },
    expireSession: reason => { if (isCurrent()) expireSessionViaBridge(reason); },
  });
  const invalidateDenied = value => {
    if (options.auth !== false && isAccessDenied(value)) {
      invalidatePrivateServiceCaches({ owner: requestOwner, credentialScope, path });
    }
  };
  try {
    const result = await client.request(path, { ...options, headers });
    invalidateDenied(result);
    return result;
  } catch (error) {
    invalidateDenied(error);
    throw error;
  }
};

// Methods that change data. Requests using these drive the global loading bar
// (see mutationActivity / GlobalLoadingBar) so users get a single, consistent
// "working…" indicator just below the top nav for every create/update/delete.
const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Wrap the shared request so any write is bracketed by begin/endMutation. GET
// requests pass straight through untracked. endMutation runs in finally so the
// bar always clears, even on error/abort.
export const request = (path, options = {}) => {
  const method = String(options.method || 'GET').toUpperCase();
  if (!MUTATION_METHODS.has(method) || options.skipMutationActivity) {
    return requestForOwner(path, options);
  }

  beginMutation();
  return Promise.resolve(requestForOwner(path, options)).finally(endMutation);
};

export const get = (path, options = {}) =>
  request(path, { ...options, method: 'GET' });

export const post = (path, body, options = {}) =>
  request(path, { ...options, method: 'POST', body });

export const put = (path, body, options = {}) =>
  request(path, { ...options, method: 'PUT', body });

export const patch = (path, body, options = {}) =>
  request(path, { ...options, method: 'PATCH', body });

// Route delete through the wrapped request so deletes are tracked too.
const del = (path, options = {}) =>
  request(path, { ...options, method: 'DELETE' });

export const requestRaw = async (path, options = {}) => {
  const { response, payload } = await request(path, {
    ...options,
    contract: 'raw',
    throwOnError: false,
  });
  return { response, data: payload };
};

const apiClient = {
  request,
  requestRaw,
  get,
  post,
  put,
  patch,
  delete: del,
};

export default apiClient;
export { ApiClientError };
